import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout, SectionTitle } from "@/components/Layout";
import { TutorialCard, AppCard, HelpBanner } from "@/components/cards";
import { Icon } from "@/components/Icon";
import { api, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ArrowLeft, ChevronRight, CheckCircle2 } from "lucide-react";

export default function DeviceDetail() {
  const { slug } = useParams();
  const { openWhatsApp } = useSettings();
  const [device, setDevice] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  const [apps, setApps] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/devices").then((r) => setDevice(r.data.find((d) => d.slug === slug)));
    api.get("/tutorials").then((r) => setTutorials(r.data));
    api.get("/apps").then((r) => setApps(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    track("device_view", slug);
  }, [slug]);

  if (!device) return <Layout><div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-gray-400">Carregando...</div></Layout>;

  const nameLower = device.name.toLowerCase();
  const relTut = tutorials.filter((t) =>
    t.device.toLowerCase().includes(nameLower) || nameLower.includes(t.device.toLowerCase()) ||
    (device.slug === "smart-tv" && ["Samsung","LG"].includes(t.category)));
  const isSmartTV = device.slug === "smart-tv";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14">
        <Link to="/dispositivos" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-cyan-300 mb-6" data-testid="back-to-devices">
          <ArrowLeft className="w-4 h-4" /> Voltar aos dispositivos
        </Link>

        <div className="flex items-center gap-4 mb-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-400/20 flex items-center justify-center">
            <Icon name={device.icon} className="w-8 h-8 text-cyan-300" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Configuração para {device.name}</h1>
            <p className="text-gray-400 mt-1">{device.description}</p>
          </div>
        </div>

        {isSmartTV && brands.length > 0 && (
          <div className="mb-14">
            <SectionTitle label="Por marca" title="Marcas de Smart TV suportadas" />
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {brands.map((b) => (
                <div key={b.id} data-testid={`brand-card-${b.name.toLowerCase().replace(/\s+/g,'-')}`} className="ci-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">{b.name}</h3>
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${
                      b.compatibility === "Alta" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                      {b.compatibility}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mb-3">Sistema: {b.system}</div>
                  <div className="text-sm text-gray-400 mb-4">
                    <span className="text-gray-300 font-medium">Apps recomendados:</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {b.recommended_apps.map((a) => <span key={a} className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10">{a}</span>)}
                    </div>
                  </div>
                  {b.tutorial_slug ? (
                    <Link to={`/tutorial/${b.tutorial_slug}`} data-testid={`brand-tutorial-${b.name.toLowerCase().replace(/\s+/g,'-')}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300 hover:text-cyan-200">
                      Ver tutorial <ChevronRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <button onClick={() => openWhatsApp(`brand-${b.name}`)} className="text-sm text-gray-400 hover:text-cyan-300">Consultar suporte</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <SectionTitle label="Tutoriais" title={`Tutoriais para ${device.name}`} />
        {relTut.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {relTut.map((t) => <TutorialCard key={t.id} tut={t} />)}
          </div>
        ) : (
          <div className="ci-card p-8 text-center text-gray-400">
            Em breve novos tutoriais para este dispositivo. Fale com o suporte para ajuda imediata.
          </div>
        )}

        <div className="mt-14">
          <HelpBanner title="Precisa de ajuda com este dispositivo?" text="Nossa equipe configura junto com você em poucos minutos." onWhatsApp={() => openWhatsApp(`device-${device.slug}`)} />
        </div>
      </div>
    </Layout>
  );
}
